// Initialize Mixpanel
import mixpanel from 'mixpanel-browser';
import { getAppVersion, getCachedAppVersion } from '@/utils/app-version';
import { REPLAY_BLOCK_SELECTOR, REPLAY_MASK_TEXT_SELECTOR } from '@/constants/replay-privacy';
import { AGM_AUTOCAPTURE_BLOCK_URL_REGEXES } from '@/constants/agm';

// Create a no-op mixpanel object for when token is not available
const createNoOpMixpanel = () => {
  const noOp = () => {};
  return {
    init: noOp,
    track: noOp,
    identify: noOp,
    alias: noOp,
    register: noOp,
    register_once: noOp,
    unregister: noOp,
    opt_in_tracking: noOp,
    opt_out_tracking: noOp,
    has_opted_in_tracking: () => false,
    has_opted_out_tracking: () => false,
    clear_opt_in_out_tracking: noOp,
    reset: noOp,
    get_distinct_id: () => null,
    get_property: () => null,
    toString: () => 'mixpanel (disabled)',
    stop_session_recording: noOp,
    start_session_recording: noOp,
    people: {
      set: noOp,
      set_once: noOp,
      unset: noOp,
      increment: noOp,
      append: noOp,
      union: noOp,
      track_charge: noOp,
      clear_charges: noOp,
      delete_user: noOp,
    },
    time_event: noOp,
    track_links: noOp,
    track_forms: noOp,
  };
};

// Check if token is available
const hasToken = !!process.env.VUE_APP_MIXPANEL_API_TOKEN;

// Use real mixpanel if token exists, otherwise use no-op version
const mixpanelInstance = hasToken ? mixpanel : createNoOpMixpanel();

// Track if Mixpanel has been initialized to prevent multiple init() calls
let isInitialized = false;
// Set true once Mixpanel's `loaded` callback has fired
let isReady = false;
// Holds the latest startMixPanel args while we wait for `loaded`
let pendingStart = null;
let appVersionPromise = null;
// This launch's version once getAppVersion() resolves; see stampAppVersion
let appVersion = null;

// Storage key for tracking aliased users
const ALIASED_USERS_KEY = 'mixpanel_aliased_users';

// Fix C — cached-identity fast path: persist last successful profile so we can
// identify Mixpanel/OneSignal at launch without waiting on Auth0 or /v2/profile.
export const LAST_MEMBER_ID_KEY = 'hogc_last_member_id';
export const LAST_PROFILE_SNAPSHOT_KEY = 'hogc_last_profile_snapshot';

// Snapshots older than this are ignored at launch. Past this window the risk
// that a shared device changed hands outweighs the identify-rate benefit.
const SNAPSHOT_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

export const persistProfileSnapshot = profile => {
  if (typeof window === 'undefined' || !profile || !profile.id) return;
  try {
    localStorage.setItem(LAST_MEMBER_ID_KEY, String(profile.id));
    localStorage.setItem(
      LAST_PROFILE_SNAPSHOT_KEY,
      JSON.stringify({
        id: profile.id,
        cgName: profile.cgName ?? null,
        zoneName: profile.zoneName ?? null,
        role: profile.role ?? null,
        dateOfBirth: profile.dateOfBirth ?? null,
        stageOfLife: profile.stageOfLife ?? null,
        educationLevel: profile.educationLevel ?? null,
        demography: profile.demography ?? null,
        isLeader: profile.isLeader ?? null,
        authSub: profile.authSub ?? null,
        persistedAt: Date.now(),
      }),
    );
    console.log('[Fix-C Mixpanel Cache] Persisted profile snapshot for', profile.id);
  } catch (e) {
    console.warn('[Fix-C Mixpanel Cache] Failed to persist profile snapshot:', e);
  }
};

export const readCachedProfile = () => {
  if (typeof window === 'undefined') return null;
  try {
    const memberId = localStorage.getItem(LAST_MEMBER_ID_KEY);
    const snapshotRaw = localStorage.getItem(LAST_PROFILE_SNAPSHOT_KEY);
    if (!memberId || !snapshotRaw) return null;
    const snapshot = JSON.parse(snapshotRaw);
    return { memberId, snapshot };
  } catch (e) {
    console.warn('[Fix-C Mixpanel Cache] Failed to read cached profile:', e);
    return null;
  }
};

export const clearCachedProfile = () => {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(LAST_MEMBER_ID_KEY);
    localStorage.removeItem(LAST_PROFILE_SNAPSHOT_KEY);
    console.log('[Fix-C Mixpanel Cache] Cleared cached profile');
  } catch (_) {
    // Ignore localStorage cleanup failures.
  }
};

// Freshness gate for the cached-identity fast path. Snapshot is stale if it
// has no timestamp (older format) or exceeds SNAPSHOT_MAX_AGE_MS.
export const isCachedProfileFresh = cached => {
  const persistedAt = cached?.snapshot?.persistedAt;
  if (!persistedAt || typeof persistedAt !== 'number') return false;
  return Date.now() - persistedAt < SNAPSHOT_MAX_AGE_MS;
};

// Called from profile-fetch once Auth0 has resolved. Wipes cached identity if
// the authenticated user.sub no longer matches the cached one — a signal that
// the device changed hands (logged-out user A, then logged-in user B).
export const validateCachedIdentityAgainstAuth = authSub => {
  if (typeof window === 'undefined' || !authSub) return;
  const cached = readCachedProfile();
  if (!cached?.snapshot?.authSub) return;
  if (cached.snapshot.authSub !== authSub) {
    console.warn(
      '[Fix-C Mixpanel Cache] authSub mismatch; wiping stale identity',
      cached.snapshot.authSub,
      '->',
      authSub,
    );
    resetMixpanelState();
  }
};

// Get list of users who have already been aliased
const getAliasedUsers = () => {
  if (typeof window === 'undefined') return new Set();
  try {
    const stored = localStorage.getItem(ALIASED_USERS_KEY);
    return stored ? new Set(JSON.parse(stored)) : new Set();
  } catch {
    return new Set();
  }
};

// Mark a user as aliased
const markUserAliased = memberId => {
  if (typeof window === 'undefined') return;
  try {
    const aliased = getAliasedUsers();
    aliased.add(memberId);
    localStorage.setItem(ALIASED_USERS_KEY, JSON.stringify([...aliased]));
  } catch (e) {
    console.warn('[Mixpanel] Could not store aliased user:', e);
  }
};

// Check if tracking should be blocked for a given path
export const shouldBlockTracking = path => {
  if (!path) return false;
  // Block tracking for sensitive routes.
  //
  // /courses/redeem carries an unlock code in the path, and an unlock card is a
  // bearer instrument — IDR's own guidance is to treat one like cash. The route
  // path is recorded as `route_path` on PAGE_VIEWED, so tracking this screen
  // would put live, unspent codes into Mixpanel and Datadog, where anyone with
  // read access could spend them. The code is in the URL because the pending
  // deep-link resume has to survive an Auth0 round trip through localStorage.
  return (
    path.startsWith('/my-giving') ||
    path.startsWith('/change-password') ||
    path.startsWith('/courses/redeem') ||
    // Same reasoning as /courses/redeem above: a leader unlock token sits in this
    // path, it is single-use and grants a paid program for free, so it is a bearer
    // instrument and must never reach an analytics payload.
    path.startsWith('/courses/unlock') ||
    // Only eligible AGM members are offered the EM briefing, so a visit to it says the
    // member is one: EM status is member data and must not reach analytics (plan D8,
    // docs/plans/2026-10-22-agm-rsvp-mobile-migration.md). /agm itself is open to every
    // member, so its page view says nothing.
    path.startsWith('/agm/briefing')
  );
};

const hasValue = value => value !== null && value !== undefined && value !== '';

const buildUserProperties = (
  memberId,
  cgName,
  zoneName,
  role,
  dob,
  stageOfLife,
  educationLevel,
  demography,
  isLeader,
) => {
  const userProperties = {
    memberId: String(memberId),
  };

  if (hasValue(cgName)) userProperties.cg = cgName;
  if (hasValue(zoneName)) userProperties.zone = zoneName;
  if (hasValue(role)) userProperties.pastoralStatus = role;
  if (hasValue(dob)) {
    try {
      const birthYear = new Date(dob).getFullYear();
      if (!isNaN(birthYear) && birthYear > 1900 && birthYear <= new Date().getFullYear()) {
        userProperties.birthYear = birthYear;
      } else {
        console.warn(`[Mixpanel] Invalid date of birth: ${dob}`);
      }
    } catch (dateError) {
      console.warn(`[Mixpanel] Error parsing date of birth: ${dob}`, dateError);
    }
  }
  if (hasValue(stageOfLife)) userProperties.phaseOfLife = stageOfLife;
  if (hasValue(educationLevel)) userProperties.educationLevel = educationLevel;
  if (hasValue(demography)) userProperties.demography = demography;
  // From /v2/profile: true while the member holds an active row in
  // member_leadership_role. A snapshot from an older build has none; leave the
  // profile as it is rather than writing false.
  if (typeof isLeader === 'boolean') userProperties.isLeader = isLeader;

  return userProperties;
};

const registerAppVersion = async () => {
  if (!hasToken) return;

  if (!appVersionPromise) {
    appVersionPromise = getAppVersion()
      .then(appInfo => {
        if (!appInfo?.version) return;
        appVersion = appInfo.version; // resolved in app-version.js: the bundle when one is running
      })
      .catch(e => {
        console.warn('[Mixpanel] Failed to resolve app version:', e);
      });
  }

  await appVersionPromise;
};

// Stamps every event, ours and autocapture's, with this launch's version.
// It used to be a registered super property, which Mixpanel persists to
// localStorage, so the events sent before getAppVersion() resolved (the first
// page view at launch) carried the version from the previous launch: a member
// who had just updated from 1.2.8 showed up as still on 1.2.8. Until the
// version is known an event says null (unknown) rather than something wrong.
const stampAppVersion = (eventName, properties) => [
  eventName,
  {
    ...properties,
    $app_version_string: appVersion || getCachedAppVersion()?.version || null,
  },
];

const ensureMixpanelInitialized = () => {
  if (!hasToken) return false;
  if (isInitialized) return true;

  try {
    mixpanelInstance.init(process.env.VUE_APP_MIXPANEL_API_TOKEN, {
      autocapture: {
        capture_extra_attrs: ['href'],
        scroll: true,
        // Nothing is autocaptured on the AGM routes: clicks, inputs, scrolls, rage and
        // dead clicks, page views. The Yes / No buttons' text is the member's answer, and
        // a click on "Add to calendar" or Babyspace says they are attending. Our own
        // Page Viewed (utils/analytics) still fires. `block_url_regexes` is the option
        // of mixpanel-browser's autocapture config (src/index.d.ts, AutocaptureConfig;
        // src/autocapture/index.js `currentUrlBlocked`), matched against the full URL.
        block_url_regexes: [...AGM_AUTOCAPTURE_BLOCK_URL_REGEXES],
      },
      ip: true,
      track_pageview: true,
      persistence: 'localStorage',
      record_sessions_percent: 100,
      record_heatmap_data: true,
      // Replays stay on, but nothing a member wrote is in them: every text node
      // under a private region (the Next Steps pages, see mixins/PrivateRegion)
      // is asterisked, and the rich-text editor is blocked outright because a
      // contenteditable leaks keystroke by keystroke otherwise. Layout, taps,
      // scrolling and heatmaps are unaffected. The selectors and the attributes
      // they match live together in constants/replay-privacy.
      // docs/plans/2026-09-02-next-steps-e2e-encryption.md, section 7.
      record_mask_text_selector: REPLAY_MASK_TEXT_SELECTOR,
      record_block_selector: REPLAY_BLOCK_SELECTOR,
      hooks: { before_track: stampAppVersion },
      loaded: () => {
        isReady = true;
        console.log('[Mixpanel] Ready');
        if (pendingStart) {
          const queued = pendingStart;
          pendingStart = null;
          runStart(queued);
        }
      },
    });
    isInitialized = true;
    // Drop the version an older build persisted; stampAppVersion owns it now.
    mixpanelInstance.unregister('$app_version_string');
    registerAppVersion();
    console.log('[Mixpanel] Initialized');
    return true;
  } catch (error) {
    console.error('[Mixpanel] Error initializing Mixpanel:', error);
    return false;
  }
};

const runStart = ([
  memberId,
  cgName,
  zoneName,
  role,
  dob,
  stageOfLife,
  educationLevel,
  demography,
  isLeader,
]) => {
  try {
    const aliasedUsers = getAliasedUsers();
    const isFirstLogin = !aliasedUsers.has(memberId);

    if (isFirstLogin) {
      const deviceId = mixpanelInstance.get_distinct_id();
      if (deviceId && deviceId !== memberId) {
        mixpanelInstance.alias(memberId, deviceId);
        markUserAliased(memberId);
        console.log(`[Mixpanel] Aliased device ${deviceId} to user ${memberId}`);
      }
    }

    mixpanelInstance.identify(memberId);
    console.log(`[Mixpanel] Identified user: ${memberId}`);

    const userProperties = buildUserProperties(
      memberId,
      cgName,
      zoneName,
      role,
      dob,
      stageOfLife,
      educationLevel,
      demography,
      isLeader,
    );

    mixpanelInstance.people.set(userProperties);
    // Field names only. The values together — cg, zone, birthYear,
    // demography — re-identify a member: a CG is 10-20 people.
    console.log('[Mixpanel] User properties set:', Object.keys(userProperties).join(', '));
  } catch (error) {
    console.error('[Mixpanel] Error setting user properties:', error);
  }
};

export const startMixPanel = async (
  memberId,
  cgName,
  zoneName,
  role,
  dob,
  stageOfLife,
  educationLevel,
  demography,
  isLeader,
) => {
  if (!hasToken) {
    console.warn('Mixpanel token not found. Analytics will be disabled.');
    return;
  }

  if (!memberId) {
    console.warn('[Mixpanel] Member ID is required. Skipping Mixpanel initialization.');
    return;
  }

  const args = [
    memberId,
    cgName,
    zoneName,
    role,
    dob,
    stageOfLife,
    educationLevel,
    demography,
    isLeader,
  ];

  try {
    if (!ensureMixpanelInitialized()) return;
    await registerAppVersion();

    if (isReady) {
      // Re-runs people.set with the latest profile data so late-arriving
      // fields (e.g. cg/zone derived after first profile response) land.
      runStart(args);
    } else {
      // Queue the most recent args; `loaded` callback will replay it.
      pendingStart = args;
    }
  } catch (error) {
    console.error('[Mixpanel] Error initializing Mixpanel:', error);
  }
};

// Fix C — launch-time identify using the last-known cached profile so that
// users who are blocked by Auth0 / profile-API failures still get their
// events attributed instead of piling up as anonymous device_ids.
// Gated on freshness: a snapshot older than SNAPSHOT_MAX_AGE_MS is discarded
// to limit the shared-device window where one user's identity could carry
// over to the next.
export const identifyFromCache = () => {
  if (!hasToken) {
    console.log('[Fix-C Mixpanel Cache] No token, skipping cached identify');
    return false;
  }
  const cached = readCachedProfile();
  if (!cached) {
    console.log('[Fix-C Mixpanel Cache] No cached profile found');
    return false;
  }
  if (!isCachedProfileFresh(cached)) {
    console.warn('[Fix-C Mixpanel Cache] Cached profile is stale (>30d or no timestamp); clearing');
    clearCachedProfile();
    return false;
  }
  // memberId only. The snapshot carries dateOfBirth and authSub.
  console.log('[Fix-C Mixpanel Cache] Identifying from cache:', cached.memberId);
  startMixPanel(
    cached.memberId,
    cached.snapshot.cgName,
    cached.snapshot.zoneName,
    cached.snapshot.role,
    cached.snapshot.dateOfBirth,
    cached.snapshot.stageOfLife,
    cached.snapshot.educationLevel,
    cached.snapshot.demography,
    cached.snapshot.isLeader,
  );
  return true;
};

// Reset all module-level init state. Used by clearAnalyticsSessions so that
// user B's login after user A's logout re-runs init() cleanly.
//
// preserveCachedProfile: keep the Fix-C snapshot (and alias memory) when the
// reset was triggered by an auth FAILURE rather than an explicit sign-out.
// A transient 401 used to wipe the snapshot, permanently breaking the
// launch-time identify fast path for that user. Identity safety on this path
// is still enforced by validateCachedIdentityAgainstAuth() (wipes on Auth0
// user mismatch) and isCachedProfileFresh() (30-day max age).
export const resetMixpanelState = ({ preserveCachedProfile = false } = {}) => {
  isInitialized = false;
  isReady = false;
  pendingStart = null;
  appVersionPromise = null;

  if (preserveCachedProfile) {
    console.log('[Fix-C Mixpanel Cache] Module state cleared; cached profile preserved');
    return;
  }

  clearCachedProfile();
  // Clear the aliased-users memory so a device that previously hosted user A
  // doesn't skip the alias step if A ever logs in again under a fresh anon id.
  if (typeof window !== 'undefined') {
    try {
      localStorage.removeItem(ALIASED_USERS_KEY);
    } catch (_) {
      // Ignore localStorage cleanup failures.
    }
  }
  console.log('[Fix-C Mixpanel Cache] Module state + cached profile + alias memory cleared');
};

// Track custom events
export const trackEvent = (eventName, properties = {}) => {
  // Check if tracking should be blocked for current route
  if (typeof window !== 'undefined' && shouldBlockTracking(window.location.pathname)) {
    console.log(
      `[Mixpanel Blocked] Tracking disabled for /my-giving/* routes: ${eventName}`,
      properties,
    );
    return;
  }

  if (!hasToken) {
    console.log(`[Mixpanel Disabled] Would track: ${eventName}`, properties);
    return;
  }

  try {
    ensureMixpanelInitialized();

    // Check if mixpanel is properly initialized
    if (!mixpanelInstance || !mixpanelInstance.config) {
      console.warn(`[Mixpanel] Not initialized yet. Skipping event: ${eventName}`);
      return;
    }

    mixpanelInstance.track(eventName, {
      ...properties,
      timestamp: new Date().toISOString(),
    });
    console.log(`✅ Tracked to Mixpanel: ${eventName}`, properties);
  } catch (error) {
    console.error('Error tracking event to Mixpanel:', error);
  }
};

// Stop Mixpanel session recording
export const stopMixpanelRecording = () => {
  if (!hasToken) {
    console.log('[Mixpanel Disabled] Would stop session recording');
    return;
  }

  try {
    // Check if mixpanel is properly initialized before calling recording methods
    if (mixpanelInstance && mixpanelInstance.config && mixpanelInstance.stop_session_recording) {
      mixpanelInstance.stop_session_recording();
      console.log('[Mixpanel] Session recording stopped');
    }
  } catch (error) {
    console.error('Error stopping Mixpanel session recording:', error);
  }
};

// Start Mixpanel session recording
export const startMixpanelRecording = () => {
  if (!hasToken) {
    console.log('[Mixpanel Disabled] Would start session recording');
    return;
  }

  try {
    ensureMixpanelInitialized();

    // Check if mixpanel is properly initialized before calling recording methods
    if (mixpanelInstance && mixpanelInstance.config && mixpanelInstance.start_session_recording) {
      mixpanelInstance.start_session_recording();
      console.log('[Mixpanel] Session recording started');
    }
  } catch (error) {
    console.error('Error starting Mixpanel session recording:', error);
  }
};

// Export mixpanel for use in other modules
export default mixpanelInstance;
